"use client";

/**
 * Whether going back would stay inside this app, or walk out of it.
 *
 * `?from=` says *where* a back press should land — it does not say that such an
 * entry exists behind the current one. Every deep link carries it: a shared
 * event URL, a push notification, the native shell's cold start, a plain
 * reload. Treating the param as proof of in-app history is what sent
 * `router.back()` out of the document entirely — off to whatever the tab or
 * WebView happened to show before, which reads as "the back button dumped me
 * on some random page".
 *
 * So count instead. Every history entry this document creates goes through
 * `history.pushState` — Next's router for route changes, `useBackNavigation`
 * for its interception entries — and every back press arrives as `popstate`.
 * Pushes minus pops is exactly how many entries sit behind us that we put
 * there, which is exactly the question `router.back()` needs answered.
 *
 * Counted rather than read off `history.length` because that number includes
 * everything the tab did before this document loaded, and never shrinks when a
 * back press moves the cursor. `history.replaceState` is deliberately not
 * counted: it rewrites the current entry and creates nothing to go back to
 * (tab switches on /hiives are replaces for this very reason).
 *
 * A forward press also arrives as `popstate` and is counted as a back, so the
 * depth can read low after a back-then-forward. That direction fails safe: a
 * too-low depth falls back to `router.push(backHref)`, which lands on the same
 * page by a different route.
 */
// The counter lives on `window`, not in a module binding: this module can be
// evaluated once per chunk that imports it, and a second module instance would
// wrap `pushState` a second time and count every entry twice.
const STATE_KEY = "__hiiveNavigationDepth";

/**
 * How many back targets are remembered. Only the top is ever read, so a full
 * stack drops its oldest entry rather than refusing to grow — the depth it
 * would describe is far past anything a back press can reach in one step.
 */
const MAX_REMEMBERED_ENTRIES = 50;

declare global {
  interface Window {
    [STATE_KEY]?: { depth: number; behind: string[] };
  }
}

function hrefNow() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function install() {
  if (typeof window === "undefined" || window[STATE_KEY]) return;

  const state: { depth: number; behind: string[] } = { depth: 0, behind: [] };
  window[STATE_KEY] = state;

  const nativePushState = window.history.pushState;
  window.history.pushState = function patchedPushState(
    this: History,
    ...args: Parameters<History["pushState"]>
  ) {
    state.depth += 1;
    // Read BEFORE the push lands: the URL showing right now is the one the new
    // entry is stacked on top of, which is exactly where a back press goes.
    state.behind.push(hrefNow());
    if (state.behind.length > MAX_REMEMBERED_ENTRIES) state.behind.shift();
    return nativePushState.apply(this, args);
  };

  // Registered at module evaluation — i.e. during hydration, before any
  // component effect — so this runs before `useBackNavigation`'s own popstate
  // listener and the depth it reads is already up to date.
  window.addEventListener("popstate", () => {
    state.depth = state.depth > 0 ? state.depth - 1 : 0;
    state.behind.pop();
  });
}

install();

/**
 * True when at least one history entry behind the current one was created by
 * this document, so `router.back()` is guaranteed to stay in the app.
 */
export function canGoBackWithinApp() {
  if (typeof window === "undefined") return false;
  return (window[STATE_KEY]?.depth ?? 0) > 0;
}

/**
 * The URL of the entry directly behind the current one — i.e. where a back
 * press would actually land — or `null` when this document put nothing there.
 *
 * `?from=` says where back *should* go; this says where it *would* go, and the
 * two disagree more often than they look like they could. Any handler that
 * reaches the current page with `router.push()` rather than `router.back()`
 * (every page-level back handler here does, whenever it cannot pop) stacks a
 * new entry whose predecessor is the page the reader just left — not the one
 * its own `?from=` names. Walking hiives → event → hiive → back → event and
 * pressing back again then popped to the hiive, while the event's `?from=`
 * said `/hiives`: a back button that lands somewhere its own arrow never
 * promised.
 *
 * Callers compare this against the resolved back href and only `back()` on an
 * exact match; anything else pushes the href, which lands in the right place
 * by the honest route. Replaces the depth check alone as the test, because a
 * non-zero depth only says *something* is behind us, never *what*.
 */
export function hrefBehindCurrentEntry(): string | null {
  if (typeof window === "undefined") return null;
  const behind = window[STATE_KEY]?.behind;
  if (!behind || behind.length === 0) return null;
  return behind[behind.length - 1] ?? null;
}
